import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Avatar } from "../../../../design_system/components/Avatar/Avatar.jsx";
import { Badge } from "../../../../design_system/components/Input Fields/Badge.jsx";
import { Button, IconButton } from "../../../../design_system/components/Button/Button.jsx";
import { ClickableDropdown } from "../../../../design_system/components/Dropdown/Dropdown.jsx";
import { DropdownItem } from "../../../../design_system/components/Dropdown/DropdownItem.jsx";
import { DropdownMenu } from "../../../../design_system/components/Dropdown/DropdownMenu.jsx";
import { DropdownTrigger } from "../../../../design_system/components/Dropdown/DropdownTrigger.jsx";
import { Icons } from "../../../../design_system/components/Icons";
import { InputBox } from "../../../../design_system/components/Input Fields/InputBox.jsx";
import { SelectBox } from "../../../../design_system/components/Input Fields/SelectBox.jsx";
import { Loader } from "../../../../design_system/components/Loader.jsx";
import { Popup } from "../../../../design_system/components/Popup/Popup.jsx";
import { Toggle } from "../../../../design_system/components/SelectionControls/Toggle.jsx";
import { Tooltip } from "../../../../design_system/components/ToolTip/ToolTip.jsx";
import { useToast } from "../../../../design_system/components/Toast/ToastContext.jsx";
import {
    availablePermissionCategories,
    permissionSubtitle,
    permissionsForCategory,
    visibilityPermissions,
} from "./permissionCatalogue";
import {
    createAccessRole,
    deleteAccessRole,
    fetchAccessRoles,
    previewAccessRole,
    updateAccessRole,
} from "./api";

const EMPTY_ROLE = {
    name: "",
    description: "",
    permission_keys: [],
};

const errorMessage = (error, fallback) =>
    error?.response?.data?.error ||
    error?.response?.data?.error_message ||
    error?.response?.data?.errors?.join(", ") ||
    fallback;

const formatEditedAt = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const formattedDate = date.getDate() + " " + months[date.getMonth()];
    return date.getFullYear() === new Date().getFullYear()
        ? formattedDate
        : formattedDate + " " + date.getFullYear();
};

const permissionMeta = (catalogue, key) =>
    catalogue.find((entry) => entry.key === key) || {
        key,
        label: key,
        group: "Permission",
        kind: "action",
    };

const sameKeys = (left, right) => {
    const first = [...(left || [])].sort();
    const second = [...(right || [])].sort();
    return first.length === second.length && first.every((key, index) => key === second[index]);
};

const changedKeyCount = (left, right) => {
    const first = new Set(left || []);
    const second = new Set(right || []);
    return [...first].filter((key) => !second.has(key)).length +
        [...second].filter((key) => !first.has(key)).length;
};

const RoleTypeBadge = ({ locked }) => (
    <div className={"role-permissions__type-group role-permissions__type-group--" + (locked ? "system" : "custom")}>
        {locked && <Icons name="lock" size={14} />}
        <Badge
            label={locked ? "System" : "Custom"}
            type={locked ? "Neutral" : "Success"}
            fontClass="support-m"
            customClassName={"role-permissions__type role-permissions__type--" + (locked ? "system" : "custom")}
        />
    </div>
);

const RoleDescription = ({ role }) => {
    const description = role.description || (role.locked ? "Built-in access for this team" : "No description");

    return (
        <Tooltip
            fontClass="support-m"
            placement="bottom-start"
            tooltipText={description}
            wrapperStyle={{ display: "block", width: "100%", minWidth: 0 }}
        >
            <span className="role-permissions__role-description support-m color-text-secondary">
                {description}
            </span>
        </Tooltip>
    );
};

const VisibilityPills = ({ role, catalogue }) => {
    const selected = new Set(role.permission_keys || []);
    return (
        <div className="role-permissions__visibility">
            {visibilityPermissions(catalogue).map((permission) => (
                <Badge
                    customClassName={
                        "role-permissions__visibility-pill" +
                        (selected.has(permission.key) ? " role-permissions__visibility-pill--enabled" : "")
                    }
                    fontClass="support-m"
                    key={permission.key}
                    label={permission.short_label || permission.label}
                    type={selected.has(permission.key) ? "Success" : "Neutral"}
                />
            ))}
        </div>
    );
};

const RoleIdentityPopup = ({ role, onClose, onSaved, onConflict }) => {
    const isNew = !role?.id;
    const toast = useToast();
    const [name, setName] = useState(role?.name || "");
    const [description, setDescription] = useState(role?.description || "");
    const [error, setError] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const save = async () => {
        if (!name.trim()) {
            setError("Role name is required");
            return;
        }

        setIsSaving(true);
        setError("");
        try {
            const response = isNew
                ? await createAccessRole({
                    name,
                    description,
                    permission_keys: [],
                })
                : await updateAccessRole(role.id, {
                    name,
                    description,
                    permission_keys: role.permission_keys,
                    lock_version: role.lock_version,
                });
            toast.success(isNew ? "Role created" : "Role details updated");
            onSaved({
                ...response.role,
                member_count: role?.member_count || 0,
                assigned_members: role?.assigned_members || [],
            });
        } catch (requestError) {
            const message = errorMessage(requestError, "Unable to save role");
            setError(message);
            toast.error(message);
            if (requestError?.response?.status === 409) await onConflict?.();
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Popup
            name={isNew ? "Create a role" : "Edit name & description"}
            maxWidth={520}
            handleOnClosePopup={onClose}
            primaryAction={{
                label: isNew ? "Create & set permissions" : "Save details",
                onClick: save,
                isLoading: isSaving,
                disabled: !name.trim(),
            }}
            secondaryAction={{ label: "Cancel", onClick: onClose }}
        >
            <div className="role-permissions__identity-popup">
                <InputBox
                    label="Role name"
                    showLabel
                    value={name}
                    handleOnChange={(event) => {
                        setName(event.target.value);
                        setError("");
                    }}
                    placeholder="For example, Events & Alumni Outreach"
                />
                <InputBox
                    label="Description (optional)"
                    showLabel
                    value={description}
                    handleOnChange={(event) => setDescription(event.target.value)}
                    placeholder="What this role is for"
                />
                {isNew && (
                    <div className="body-s color-text-secondary">
                        A new role starts with nothing switched on. You pick its permissions next.
                    </div>
                )}
                {error && <div className="body-s role-permissions__error">{error}</div>}
            </div>
        </Popup>
    );
};

const ChangeRow = ({ type, permission, categories }) => (
    <div className="role-permissions__change-row">
        <span className={"role-permissions__change-icon role-permissions__change-icon--" + type}>
            <Icons name={type === "added" ? "plus" : "minus"} size={16} />
        </span>
        <div className="role-permissions__change-copy">
            <span className="body-s-bold color-text-primary">{permission.label}</span>
            <span className="body-s color-text-secondary">
                {permissionSubtitle(permission, categories)}
            </span>
        </div>
        <span className="body-s color-text-secondary">
            {type === "added" ? "Off → On" : "On → Off"}
        </span>
    </div>
);

const PermissionEditor = ({ role, catalogue, categories, onBack, onUpdated }) => {
    const toast = useToast();
    const editorRef = useRef(null);
    const readOnly = Boolean(role.locked);
    const [permissionKeys, setPermissionKeys] = useState([...(role.permission_keys || [])]);
    const [activeCategory, setActiveCategory] = useState("");
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isPermissionPanelExpanded, setIsPermissionPanelExpanded] = useState(true);
    const selected = useMemo(() => new Set(permissionKeys), [permissionKeys]);
    const dirty = !sameKeys(permissionKeys, role.permission_keys);
    const unsavedChangeCount = changedKeyCount(permissionKeys, role.permission_keys);

    useLayoutEffect(() => {
        const scrollContainer = editorRef.current?.closest(".settings-popup__content");
        if (scrollContainer) scrollContainer.scrollTop = 0;
    }, [role.id]);

    const availableCategories = useMemo(
        () => availablePermissionCategories(catalogue, categories),
        [catalogue, categories]
    );
    const activeDefinition = availableCategories.find((category) => category.key === activeCategory) || availableCategories[0];
    useEffect(() => {
        if (activeDefinition && activeDefinition.key !== activeCategory) {
            setActiveCategory(activeDefinition.key);
        }
    }, [activeCategory, activeDefinition]);
    const activePermissions = useMemo(
        () => activeDefinition ? permissionsForCategory(catalogue, activeDefinition.key) : [],
        [catalogue, activeDefinition]
    );
    const visiblePermissions = activePermissions.filter((permission) => permission.kind === "visibility");
    const actionPermissions = activePermissions.filter((permission) => permission.kind !== "visibility");

    const togglePermission = (key) => {
        if (readOnly || isSaving) return;
        setPermissionKeys((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return catalogue.filter((entry) => next.has(entry.key)).map((entry) => entry.key);
        });
        setPreview(null);
        setError("");
    };

    const review = async () => {
        if (!dirty) return;
        setIsSaving(true);
        setError("");
        try {
            const changes = await previewAccessRole(role.id, permissionKeys);
            if (changes.assigned_member_count === 0 ||
                changes.role_permissions_added.length + changes.role_permissions_removed.length === 0) {
                await persist();
            } else {
                setPreview(changes);
            }
        } catch (requestError) {
            const message = errorMessage(requestError, "Unable to review role changes");
            setError(message);
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    const persist = async () => {
        setIsSaving(true);
        setError("");
        try {
            const response = await updateAccessRole(role.id, {
                name: role.name,
                description: role.description,
                permission_keys: permissionKeys,
                lock_version: role.lock_version,
            });
            toast.success("Role updated");
            setPreview(null);
            await onUpdated(response.role);
        } catch (requestError) {
            const message = errorMessage(requestError, "Unable to update role");
            setError(message);
            toast.error(message);
            if (requestError?.response?.status === 409) await onUpdated();
        } finally {
            setIsSaving(false);
        }
    };

    const changeCount = preview
        ? preview.role_permissions_added.length + preview.role_permissions_removed.length
        : 0;
    const previewMembers = preview?.assigned_members || preview?.affected_members || [];

    const renderPermissionSection = (title, description, permissions) => {
        if (permissions.length === 0) return null;
        return (
            <>
                <div className="role-permissions__kind-header">
                    <span className="role-permissions__kind-title label-s">{title}</span>
                    <span className="role-permissions__kind-description label-s color-text-secondary">{description}</span>
                </div>
                {permissions.map((permission) => (
                    <div className="role-permissions__permission-row" key={permission.key}>
                        <span className="label-m color-text-primary">{permission.label}</span>
                        <Toggle
                            aria-label={permission.label}
                            checked={selected.has(permission.key)}
                            disabled={readOnly || isSaving}
                            onChange={() => togglePermission(permission.key)}
                        />
                    </div>
                ))}
            </>
        );
    };

    return (
        <div className="settings-panel role-permissions role-permissions--editor" ref={editorRef}>
            <div className="role-permissions__breadcrumb">
                <button type="button" onClick={onBack}>Roles & Permissions</button>
                <Icons name="chevron-right" size={16} />
                <span className="color-text-primary">{role.name}</span>
            </div>

            <div className="role-permissions__editor-heading">
                <div>
                    <div className="role-permissions__title-line">
                        <h2 className="headline-s">{role.name}</h2>
                        <RoleTypeBadge locked={readOnly} />
                    </div>
                    <div className="body-s color-text-secondary">
                        Held by {role.member_count || 0} member{role.member_count === 1 ? "" : "s"}
                        {readOnly ? " · Built-in role" : formatEditedAt(role.updated_at) ? " · Last edited " + formatEditedAt(role.updated_at) : ""}
                    </div>
                    {dirty && (
                        <div className="body-s role-permissions__unsaved">
                            {unsavedChangeCount} unsaved change{unsavedChangeCount === 1 ? "" : "s"}
                        </div>
                    )}
                </div>
                {!readOnly && (
                    <Button Size="Small" onClick={review} isLoading={isSaving} disabled={!dirty}>
                        Save changes
                    </Button>
                )}
            </div>

            <div className="role-permissions__category-tabs" role="tablist" aria-label="Permission categories">
                {availableCategories.map((category) => {
                    const entries = permissionsForCategory(catalogue, category.key);
                    const enabled = entries.filter((permission) => selected.has(permission.key)).length;
                    return (
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeCategory === category.key}
                            className={"role-permissions__category-tab" + (activeCategory === category.key ? " role-permissions__category-tab--active" : "")}
                            key={category.key}
                            onClick={() => setActiveCategory(category.key)}
                        >
                            <span className="role-permissions__category-label label-s">{category.label}</span>
                            <span className="role-permissions__category-count support-m">{enabled}/{entries.length}</span>
                        </button>
                    );
                })}
            </div>

            <section className="role-permissions__permission-panel">
                <button
                    type="button"
                    className="role-permissions__permission-panel-heading"
                    aria-expanded={isPermissionPanelExpanded}
                    aria-controls="active-role-permissions"
                    onClick={() => setIsPermissionPanelExpanded((isExpanded) => !isExpanded)}
                >
                    <span className="body-m-semibold">{activeDefinition?.label || "Permissions"}</span>
                    <span className="role-permissions__permission-panel-summary">
                        <span className="body-s color-text-secondary">
                            {activePermissions.filter((permission) => selected.has(permission.key)).length} of {activePermissions.length} on
                        </span>
                        <Icons name={isPermissionPanelExpanded ? "chevron-up" : "chevron-down"} size={16} />
                    </span>
                </button>
                {isPermissionPanelExpanded && (
                    <div id="active-role-permissions">
                        {renderPermissionSection(
                            "Can see",
                            "Hidden here means hidden in search and alerts too",
                            visiblePermissions
                        )}
                        {renderPermissionSection(
                            "Can do",
                            "Actions on content members can already see",
                            actionPermissions
                        )}
                    </div>
                )}
            </section>

            {error && <div className="body-s role-permissions__error">{error}</div>}

            {preview && (
                <Popup
                    name={"Apply " + changeCount + " change" + (changeCount === 1 ? "" : "s") + " to " + preview.assigned_member_count + " member" + (preview.assigned_member_count === 1 ? "" : "s") + "?"}
                    maxWidth={580}
                    customClassName="role-permissions__apply-popup"
                    handleOnClosePopup={() => setPreview(null)}
                    primaryAction={{ label: "Apply changes", onClick: persist, isLoading: isSaving }}
                    secondaryAction={{ label: "Keep editing", onClick: () => setPreview(null) }}
                >
                    <div className="role-permissions__preview">
                        <p className="body-s color-text-secondary">
                            Everyone holding {role.name} is updated the moment you save. Saved items remain with each member.
                        </p>
                        <div className="role-permissions__change-list">
                            {preview.role_permissions_added.map((key) => (
                                <ChangeRow type="added" permission={permissionMeta(catalogue, key)} categories={categories} key={key} />
                            ))}
                            {preview.role_permissions_removed.map((key) => (
                                <ChangeRow type="removed" permission={permissionMeta(catalogue, key)} categories={categories} key={key} />
                            ))}
                        </div>
                        {previewMembers.length > 0 && (
                            <div>
                                <div className="body-s-bold color-text-primary mb-s">Who this affects</div>
                                <div className="role-permissions__member-chips">
                                    {previewMembers.map((member) => (
                                        <span className="role-permissions__member-chip" key={member.id}>
                                            <Avatar name={member.name || member.email} width={24} textClassName="support-m-semibold" />
                                            <span className="body-s">{member.name || member.email}</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </Popup>
            )}
        </div>
    );
};

const DeleteRolePopup = ({ role, customRoles, onClose, onDeleted, onConflict }) => {
    const toast = useToast();
    const [replacementRoleId, setReplacementRoleId] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState("");
    const members = role.assigned_members || [];
    const replacementOptions = [
        { label: "Regular — the default everyone is invited on", value: "" },
        ...customRoles
            .filter((candidate) => candidate.id !== role.id)
            .map((candidate) => ({ label: candidate.name, value: candidate.id })),
    ];
    const replacement = customRoles.find((candidate) => candidate.id === replacementRoleId);

    const remove = async () => {
        setIsDeleting(true);
        setError("");
        try {
            await deleteAccessRole(role.id, replacementRoleId || null, role.lock_version);
            toast.success("Role deleted");
            await onDeleted();
        } catch (requestError) {
            const message = errorMessage(requestError, "Unable to delete role");
            setError(message);
            toast.error(message);
            if (requestError?.response?.status === 409) await onConflict?.();
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Popup
            name={"Delete “" + role.name + "”?"}
            maxWidth={560}
            handleOnClosePopup={onClose}
            primaryAction={{
                label: members.length > 0
                    ? "Delete role & move " + members.length + " " + (members.length === 1 ? "person" : "people")
                    : "Delete role",
                Type: "Danger",
                onClick: remove,
                isLoading: isDeleting,
            }}
            secondaryAction={{ label: "Keep the role", onClick: onClose }}
        >
            <div className="role-permissions__delete">
                {members.length > 0 && (
                    <>
                        <div className="role-permissions__warning">
                            <Icons name="warning" size={16} />
                            <div>
                                <div className="body-s-bold color-text-primary">{members.length} {members.length === 1 ? "person holds" : "people hold"} this role right now</div>
                                <div className="body-s color-text-secondary">
                                    A role is only a saved set of permissions, so deleting it removes no member data. Select where these members should land.
                                </div>
                            </div>
                        </div>
                        <SelectBox
                            label={"Move " + (members.length === 1 ? "this person" : "these people") + " to"}
                            showLabel
                            value={replacementRoleId}
                            options={replacementOptions}
                            handleOnChange={setReplacementRoleId}
                            Size="Large"
                            align="left"
                            containerClass="role-permissions__replacement-select"
                            customStyle={{ width: "100%" }}
                            triggerStyle={{
                                border: "1px solid var(--color-border-subtle)",
                                backgroundColor: "var(--color-surface-page)",
                            }}
                        />
                        <div className="role-permissions__delete-members">
                            {members.map((member) => (
                                <div className="role-permissions__delete-member" key={member.id}>
                                    <Avatar name={member.name || member.email} width={32} />
                                    <div className="role-permissions__delete-member-copy">
                                        <span className="body-s-bold">{member.name || member.email}</span>
                                        <span className="body-s color-text-secondary">{member.email}</span>
                                    </div>
                                    <span className="role-permissions__source-role-pill support-m">{role.name}</span>
                                    <Icons name="chevron-right" size={16} />
                                    <span className="role-permissions__replacement-pill support-m">
                                        {replacement?.name || "Regular"}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div className="body-s color-text-secondary">
                            Changing roles clears all individual exceptions on these members.
                        </div>
                    </>
                )}
                {members.length === 0 && (
                    <p className="body-m color-text-secondary">
                        No members currently hold this role. Deleting it will only remove the saved permission set.
                    </p>
                )}
                {error && <div className="body-s role-permissions__error">{error}</div>}
            </div>
        </Popup>
    );
};

const RolePermissions = ({ setActiveSettingTab }) => {
    const toast = useToast();
    const showLoadError = toast.error;
    const [data, setData] = useState({
        permission_catalogue: [],
        permission_categories: [],
        system_roles: [],
        custom_roles: [],
    });
    const [isLoading, setIsLoading] = useState(true);
    const [editorRole, setEditorRole] = useState(null);
    const [identityRole, setIdentityRole] = useState(null);
    const [deleteRole, setDeleteRole] = useState(null);

    const loadRoles = useCallback(async () => {
        setIsLoading(true);
        try {
            setData(await fetchAccessRoles());
        } catch (error) {
            showLoadError(errorMessage(error, "Unable to load roles"));
        } finally {
            setIsLoading(false);
        }
    }, [showLoadError]);

    useEffect(() => {
        setActiveSettingTab?.("roles_permissions");
        loadRoles();
    }, [loadRoles, setActiveSettingTab]);

    const allRoles = useMemo(
        () => [...data.system_roles, ...data.custom_roles],
        [data]
    );

    const openEditor = (role) => setEditorRole(role);

    const identitySaved = async (savedRole) => {
        setIdentityRole(null);
        if (!savedRole.id) return;
        await loadRoles();
        setEditorRole(savedRole);
    };

    if (editorRole) {
        return (
            <PermissionEditor
                key={editorRole.id}
                role={editorRole}
                catalogue={data.permission_catalogue}
                categories={data.permission_categories}
                onBack={() => setEditorRole(null)}
                onUpdated={async () => {
                    setEditorRole(null);
                    await loadRoles();
                }}
            />
        );
    }

    return (
        <div className="settings-panel role-permissions">
            <div className="settings-panel__header">
                <div className="body-l-bold settings-panel__title">Roles & Permissions</div>
                <div className="role-permissions__header-row">
                    <div className="settings-panel__description body-s-semibold">
                        Decide what each person on the team can see and do. Admin and Regular are built in and cannot be edited. Create a custom role for anything else.
                    </div>
                    <div className="role-permissions__header-actions">
                        <Button Size="Small" onClick={() => setIdentityRole(EMPTY_ROLE)}>
                            Create Role
                        </Button>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="role-permissions__loading"><Loader isLoading /></div>
            ) : (
                <div className="role-permissions__table-wrap">
                    <table className="role-permissions__table">
                        <thead>
                            <tr>
                                <th className="body-s-semibold">Role</th>
                                <th className="body-s-semibold">Data types it can see</th>
                                <th className="body-s-semibold">Members</th>
                                <th className="body-s-semibold">Type</th>
                                <th aria-label="Actions"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {allRoles.map((role) => (
                                <tr key={role.id}>
                                    <td>
                                        <button
                                            type="button"
                                            className="role-permissions__role-link"
                                            onClick={() => openEditor(role)}
                                        >
                                            <span className="label-l">{role.name}</span>
                                            <RoleDescription role={role} />
                                        </button>
                                    </td>
                                    <td><VisibilityPills role={role} catalogue={data.permission_catalogue} /></td>
                                    <td><span className="body-s color-text-secondary">{role.member_count || 0} member{role.member_count === 1 ? "" : "s"}</span></td>
                                    <td><RoleTypeBadge locked={role.locked} /></td>
                                    <td className="role-permissions__actions-cell">
                                        <ClickableDropdown>
                                            <DropdownTrigger ShowHover={false} IsIconVisible={false} customStyle={{ padding: 0 }}>
                                                <IconButton Size="ExtraSmall" Type="Tertiary" IconName="vertical-ellipsis" ariaLabel={"Actions for " + role.name} />
                                            </DropdownTrigger>
                                            <DropdownMenu usePortal align="right">
                                                <DropdownItem IconName={role.locked ? "eye-show" : "pencil"} onClick={() => openEditor(role)}>
                                                    {role.locked ? "View permissions" : "Edit permissions"}
                                                </DropdownItem>
                                                {!role.locked && (
                                                    <DropdownItem IconName="edit" onClick={() => setIdentityRole(role)}>
                                                        Edit name & description
                                                    </DropdownItem>
                                                )}
                                                {!role.locked && (
                                                    <DropdownItem IconName="delete" onClick={() => setDeleteRole(role)} customClasses="dropdown-item-danger">
                                                        Delete role
                                                    </DropdownItem>
                                                )}
                                            </DropdownMenu>
                                        </ClickableDropdown>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {allRoles.length === 0 && (
                        <div className="role-permissions__empty body-m-semibold color-text-secondary">
                            No roles available.
                        </div>
                    )}
                </div>
            )}

            {!isLoading && <div className="role-permissions__info">
                <Icons name="info" size={16} />
                <div>
                    <div className="body-s-bold color-text-primary">Roles cover the pattern. Exceptions cover the person.</div>
                    <div className="body-s color-text-secondary">
                        When one teammate needs something slightly different, keep their role and add an exception on the People tab — no need to create a role for one person.
                    </div>
                </div>
            </div>}

            {identityRole && (
                <RoleIdentityPopup
                    role={identityRole}
                    onClose={() => setIdentityRole(null)}
                    onSaved={identitySaved}
                    onConflict={async () => {
                        setIdentityRole(null);
                        await loadRoles();
                    }}
                />
            )}

            {deleteRole && (
                <DeleteRolePopup
                    role={deleteRole}
                    customRoles={data.custom_roles}
                    onClose={() => setDeleteRole(null)}
                    onDeleted={async () => {
                        setDeleteRole(null);
                        await loadRoles();
                    }}
                    onConflict={async () => {
                        setDeleteRole(null);
                        await loadRoles();
                    }}
                />
            )}
        </div>
    );
};

export default RolePermissions;
